import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { SelectField, SwitchField } from '../components/fields'
import ErrorTryAgain from '../components/ErrorTryAgain'
import { WrappedLoading } from '../components/Loading'
import { Button, Icon, Link } from '../components'
import { setBulkOrderGridFullWidth } from '../../common/app/actions'
import {
  BULK_ORDER_GRID_AXES,
  fetchBulkOrderLensDetail,
  filterAdditionGridBySign,
  getDisplayLinePowers,
  hasBothAdditionSigns,
  isBulkOrderSideEnabled,
  setBulkOrderGridContext,
  selectCartLineCount,
  selectGridContext,
  selectIsCartEditable,
} from '../../common/bulkOrder'
import BulkOrderProductRecapPanel from './BulkOrderProductRecapPanel'
import BulkOrderSphCylGrid from './BulkOrderSphCylGrid'
import { formatCartCountLabel } from './bulkOrderLabels'
import { getRecapChangedControls, getRecapGridContextPatch } from './recapFocusUtils'
import useBulkOrderGridDerived from './useBulkOrderGridDerived'
import useBulkOrderGridQuantities from './useBulkOrderGridQuantities'
import {
  Panel,
  PanelHeader,
  ProductTitle,
  ProductTitleRow,
  FullWidthToggle,
  ControlsRow,
  PulsingControlField,
  PulsingSwitchControl,
  PanelBody,
  LeftPane,
  RecapPane,
  RecapFooter,
  RecapScrollArea,
  GridSection,
  EmptyState,
  EmptyStateContent,
  EmptyStateText,
  CONTROL_PULSE_DURATION_MS,
} from './BulkOrderGridPanel.styles'
import { LENS_DETAIL_LOADER_DELAY_MS, SIGN_SWITCH_OPTIONS, SIDE_SWITCH_OPTIONS } from './config'

const BulkOrderGridPanel = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const selectedLensId = useSelector(state => state.bulkOrder.selectedLensId)
  const lensDetail = useSelector(state => state.bulkOrder.lensDetail)
  const isLensDetailLoading = useSelector(state => state.bulkOrder.isLensDetailLoading)
  const hasLensDetailError = useSelector(state => state.bulkOrder.hasLensDetailError)
  const isCartEditable = useSelector(selectIsCartEditable)
  const gridContext = useSelector(selectGridContext)
  const cartLineCount = useSelector(selectCartLineCount)
  const bulkOrderGridFullWidth = useSelector(state => state.app.bulkOrderGridFullWidth)
  const { handleQuantityChange } = useBulkOrderGridQuantities()
  const [pendingFocus, setPendingFocus] = useState(null)
  const [pulsingControls, setPulsingControls] = useState([])
  const [showDelayedLoader, setShowDelayedLoader] = useState(false)

  const { diameter: selectedDiameter, side, cylinderSign, additionSign } = gridContext
  const { quantities, recap } = useBulkOrderGridDerived({
    selectedLensId,
    lensGridAxis: lensDetail?.gridAxis,
    diameter: selectedDiameter,
    side,
    cylinderSign,
  })
  const hasSideSelection = lensDetail?.hasSideSelection === true
  const showSide = isBulkOrderSideEnabled() && hasSideSelection
  const gridAxis = lensDetail?.gridAxis || BULK_ORDER_GRID_AXES.CYLINDER
  const isAdditionGrid = gridAxis === BULK_ORDER_GRID_AXES.ADDITION
  const isLensDetailStale =
    !lensDetail || String(lensDetail._id) !== String(selectedLensId)
  const isWaitingForLensDetail = isLensDetailLoading && isLensDetailStale

  useEffect(() => {
    if (!isWaitingForLensDetail) {
      setShowDelayedLoader(false)
      return undefined
    }

    const timerId = setTimeout(() => {
      setShowDelayedLoader(true)
    }, LENS_DETAIL_LOADER_DELAY_MS)

    return () => clearTimeout(timerId)
  }, [isWaitingForLensDetail])

  const handleRetryLensDetail = useCallback(() => {
    if (selectedLensId) {
      dispatch(fetchBulkOrderLensDetail(selectedLensId))
    }
  }, [dispatch, selectedLensId])

  const diameterOptions = useMemo(
    () =>
      (lensDetail?.diameters || []).map(diameterItem => ({
        value: diameterItem.physical,
        label: `${diameterItem.physical}${
          diameterItem.optical ? ` / ${diameterItem.optical}` : ''
        }`,
      })),
    [lensDetail?.diameters],
  )

  const diameterGrid = useMemo(() => {
    if (!lensDetail?.gridsByDiameter || selectedDiameter == null) return null
    return lensDetail.gridsByDiameter[String(selectedDiameter)] || null
  }, [lensDetail?.gridsByDiameter, selectedDiameter])

  const activeGrid = useMemo(() => {
    if (!diameterGrid) return null
    if (isAdditionGrid) return filterAdditionGridBySign(diameterGrid, additionSign)
    return diameterGrid[cylinderSign] || null
  }, [diameterGrid, cylinderSign, additionSign, isAdditionGrid])

  const handleRecapLineClick = useCallback(
    line => {
      const changedControls = getRecapChangedControls(line, gridContext, {
        isAdditionGrid,
        isSideEnabled: showSide,
      })
      const hasContextChange = changedControls.length > 0
      const token = Date.now()

      dispatch(
        setBulkOrderGridContext(
          getRecapGridContextPatch(line, { hasSideSelection, isAdditionGrid }),
        ),
      )

      const display = getDisplayLinePowers(
        line,
        line.gridAxis || gridAxis,
        gridContext.cylinderSign,
      )

      setPendingFocus({
        sphere: display.sphere,
        columnValue: display.columnValue,
        token,
        hasContextChange,
      })

      if (hasContextChange) {
        setPulsingControls(changedControls)
      }
    },
    [dispatch, gridContext, gridAxis, hasSideSelection, isAdditionGrid, showSide],
  )

  const handleFocusComplete = useCallback(token => {
    setPendingFocus(current => current?.token === token ? null : current)
  }, [])

  useEffect(() => {
    if (pulsingControls.length === 0) return undefined

    const timer = setTimeout(() => {
      setPulsingControls([])
    }, CONTROL_PULSE_DURATION_MS)

    return () => clearTimeout(timer)
  }, [pulsingControls])

  const cartButtonLabel = formatCartCountLabel(t('cart'), cartLineCount)

  const renderSelectProductEmptyState = () => (
    <Panel>
      <EmptyState>
        <EmptyStateContent>
          <EmptyStateText>
            {t('Start by selecting a product from the catalog on the left.')}
          </EmptyStateText>
          {cartLineCount > 0 && (
            <Link to={Link.BULK_ORDER_CART}>
              <Button leftIcon="IosCart" rightIcon="IosArrowForward">
                {cartButtonLabel}
              </Button>
            </Link>
          )}
        </EmptyStateContent>
      </EmptyState>
    </Panel>
  )

  if (!selectedLensId) {
    return renderSelectProductEmptyState()
  }

  // Cold fetch: empty panel until delay, then loader. Switch: keep stale grid until delay.
  if (isLensDetailLoading && !lensDetail) {
    if (showDelayedLoader) {
      return (
        <Panel>
          <WrappedLoading type="puff" padding="2rem" />
        </Panel>
      )
    }
    return <Panel />
  }

  if (hasLensDetailError) {
    return (
      <Panel>
        <ErrorTryAgain onClick={handleRetryLensDetail} />
      </Panel>
    )
  }

  if (!lensDetail) {
    return renderSelectProductEmptyState()
  }

  // Slow switch: replace stale product with the same delayed full-panel loader.
  if (isWaitingForLensDetail && showDelayedLoader) {
    return (
      <Panel>
        <WrappedLoading type="puff" padding="2rem" />
      </Panel>
    )
  }

  return (
    <Panel>
      <PanelBody>
        <LeftPane>
          <PanelHeader>
            <ProductTitleRow>
              {lensDetail?.name && <ProductTitle>{lensDetail.name}</ProductTitle>}
              <FullWidthToggle>
                <Button
                  onClick={() => dispatch(setBulkOrderGridFullWidth(!bulkOrderGridFullWidth))}
                >
                  <Icon size={16} name="CustomOppositeArrows" />
                </Button>
              </FullWidthToggle>
            </ProductTitleRow>

            <ControlsRow>
              <PulsingControlField $pulse={pulsingControls.includes('diameter')}>
                <SelectField
                  name="bulkOrderDiameter"
                  label={t('diameter')}
                  value={selectedDiameter}
                  options={diameterOptions}
                  classNamePrefix="bulkOrderSelect"
                  onChange={({ value }) => dispatch(setBulkOrderGridContext({ diameter: value }))}
                />
              </PulsingControlField>

              {showSide && (
                <PulsingSwitchControl $pulse={pulsingControls.includes('side')}>
                  <SwitchField
                    name="bulkOrderSide"
                    label={t('side')}
                    value={side}
                    options={SIDE_SWITCH_OPTIONS}
                    required
                    hideRequiredStar
                    onChange={({ value }) => dispatch(setBulkOrderGridContext({ side: value }))}
                  />
                </PulsingSwitchControl>
              )}

              {!isAdditionGrid && (
                <SwitchField
                  name="bulkOrderCylinderSign"
                  label={t('cylinder')}
                  value={cylinderSign}
                  options={SIGN_SWITCH_OPTIONS}
                  required
                  hideRequiredStar
                  onChange={({ value }) =>
                    dispatch(setBulkOrderGridContext({ cylinderSign: value }))
                  }
                />
              )}
              {isAdditionGrid && hasBothAdditionSigns(diameterGrid?.additions) && (
                <PulsingSwitchControl $pulse={pulsingControls.includes('additionSign')}>
                  <SwitchField
                    name="bulkOrderAdditionSign"
                    label={t('addition')}
                    value={additionSign}
                    options={SIGN_SWITCH_OPTIONS}
                    required
                    hideRequiredStar
                    onChange={({ value }) =>
                      dispatch(setBulkOrderGridContext({ additionSign: value }))
                    }
                  />
                </PulsingSwitchControl>
              )}
            </ControlsRow>
          </PanelHeader>

          <GridSection>
            <BulkOrderSphCylGrid
              grid={activeGrid}
              gridAxis={gridAxis}
              quantities={quantities}
              pendingFocus={pendingFocus}
              isCartEditable={isCartEditable}
              onQuantityChange={handleQuantityChange}
              onFocusComplete={handleFocusComplete}
            />
          </GridSection>
        </LeftPane>

        <RecapPane>
          <RecapScrollArea>
            <BulkOrderProductRecapPanel
              groups={recap.groups}
              lineCount={recap.lineCount}
              totalQuantity={recap.totalQuantity}
              gridAxis={gridAxis}
              onLineClick={handleRecapLineClick}
            />
          </RecapScrollArea>
          {cartLineCount > 0 && (
            <RecapFooter>
              <Link to={Link.BULK_ORDER_CART}>
                <Button leftIcon="IosCart" rightIcon="IosArrowForward">
                  {cartButtonLabel}
                </Button>
              </Link>
            </RecapFooter>
          )}
        </RecapPane>
      </PanelBody>
    </Panel>
  )
}

export default React.memo(BulkOrderGridPanel)
